import { useSentryMutation, useSentryQuery } from '../utils/sentryQueryWrapper';
import {
  ContactsResponse,
  ContactRequest,
  responseContactByUid,
  ContactImportPreviewResponse,
  ContactImportCommitResponse,
} from '@/types';
import {
  createContact,
  deleteContactById,
  fetchContactById,
  fetchContacts,
  updateContactById,
  bulkDeleteContacts,
  previewContactImport,
  commitContactImport,
} from '@/api/contactService';
import { useContactsStore } from '@/stores/contactStore';
import { ErrorMessageResponse } from '@/types/error';
import { t } from 'i18next';

// Fetch Contacts (paginated)
export const useFetchContacts = (
  page = 1,
  perPage = 20,
  search?: string,
  regionUid?: string,
) => {
  const setContacts = useContactsStore((state) => state.setContacts);

  return useSentryQuery<ContactsResponse, Error>(
    ['contacts', page, perPage, search, regionUid],
    () => fetchContacts(page, perPage, search, regionUid),
    {
      onSuccess: (data) => {
        setContacts(data);
      },
      keepPreviousData: true,
    },
  );
};

// Create contact
export const useCreateContact = () => {
  return useSentryMutation<ContactsResponse, ErrorMessageResponse, ContactRequest>(
    (data: ContactRequest) => createContact(data),
    {
      defaultErrorMessage: t('failed_to_add_contact'),
    },
  );
};

// Fetch contact by ID
export const useFetchContact = (id: string) => {
  return useSentryQuery<responseContactByUid, ErrorMessageResponse>(
    ['contacts', id],
    () => fetchContactById(id),
    {
      keepPreviousData: true,
      enabled: !!id,
    },
  );
};

// Update contact By ID
export const useUpdateContact = (id: string) => {
  return useSentryMutation<ContactsResponse, Error, ContactRequest>((data) =>
    updateContactById(id, data),
  );
};

// Delete contact By ID
export const useDeleteContact = () => {
  return useSentryMutation<ContactsResponse, ErrorMessageResponse, string>(
    (id: string) => deleteContactById(id),
    { defaultErrorMessage: t('failed_to_remove_contact') },
  );
};

// Bulk delete contacts
export const useBulkDeleteContacts = () => {
  return useSentryMutation<void, ErrorMessageResponse, string[]>(
    (uids: string[]) => bulkDeleteContacts(uids),
    { defaultErrorMessage: t('failed_to_remove_contact') },
  );
};

// Preview contact import from CSV
export const usePreviewContactImport = () => {
  return useSentryMutation<ContactImportPreviewResponse, ErrorMessageResponse, File>(
    (file: File) => previewContactImport(file),
  );
};

// Commit contact import from CSV
export const useCommitContactImport = () => {
  return useSentryMutation<ContactImportCommitResponse, ErrorMessageResponse, File>(
    (file: File) => commitContactImport(file),
  );
};
